import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Zap, Rss, LayoutGrid, Users, UserCheck, Gift, BookOpen } from "lucide-react";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import NetworkProfileTab from "../components/NetworkProfileTab";
import DealRoomPanel from "../components/DealRoomPanel";
import { FeedTab }       from "../components/network/FeedTab";
import { DealBoardTab }  from "../components/network/DealBoardTab";
import { PartnersTab }   from "../components/network/PartnersTab";
import { MyNetworkTab }  from "../components/network/MyNetworkTab";
import { ReferralsTab }  from "../components/network/ReferralsTab";
import { DirectoryTab }  from "../components/network/DirectoryTab";

const TABS = [
  { key: "feed",      label: "Feed",       icon: Rss        },
  { key: "deals",     label: "Deal Board", icon: LayoutGrid },
  { key: "directory", label: "Directory",  icon: BookOpen   },
  { key: "partners",  label: "Partners",   icon: Users      },
  { key: "network",   label: "My Network", icon: UserCheck  },
  { key: "referrals", label: "Referrals",  icon: Gift       },
  { key: "profile",   label: "Profile",    icon: Globe      },
];

export default function NetworkHubPage({ user, subscription, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "feed";
  const [selectedDeal, setSelectedDeal] = useState(null);
  const { mainClass } = useSidebar();

  const setTab = (tab) => setSearchParams({ tab });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      <main className={mainClass}>

        {/* Top bar */}
        <div className="border-b border-white/[0.06] bg-[#050505]/90 backdrop-blur-sm sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center">
                <Globe className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-white font-bold text-base tracking-tight">DealForge <span className="text-[#D4AF37]">Network</span></h1>
                <p className="text-white/25 text-[10px] uppercase tracking-[0.12em]">Your real estate ecosystem</p>
              </div>
            </div>
            <a href="/network-credits" className="text-xs px-3 py-1.5 border border-[#D4AF37]/20 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/[0.06] transition-all flex items-center gap-1.5 font-medium">
              <Zap className="w-3 h-3" /> Credits
            </a>
          </div>

          {/* Tab nav */}
          <div className="max-w-4xl mx-auto px-2 sm:px-6">
            <div className="flex gap-0 overflow-x-auto scrollbar-none">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.key} onClick={() => setTab(tab.key)}
                    data-testid={`network-tab-${tab.key}`}
                    className={`flex items-center gap-1.5 px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.key
                        ? "text-white border-[#D4AF37]"
                        : "text-white/40 border-transparent hover:text-white"
                    }`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={
          activeTab === "feed"    ? "" :
          activeTab === "profile" ? "" :
          "max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6"
        }>
          {activeTab === "feed"      && <FeedTab user={user} onDealClick={setSelectedDeal} />}
          {activeTab === "deals"     && <DealBoardTab user={user} onDealClick={setSelectedDeal} />}
          {activeTab === "directory" && <DirectoryTab />}
          {activeTab === "partners"  && <PartnersTab />}
          {activeTab === "network"   && <MyNetworkTab user={user} />}
          {activeTab === "referrals" && <ReferralsTab user={user} />}
          {activeTab === "profile"   && <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6 overflow-hidden"><NetworkProfileTab user={user} /></div>}
        </div>
      </main>

      {selectedDeal && (
        <DealRoomPanel
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          isOwner={selectedDeal.user_id === user?.user_id}
        />
      )}
    </div>
  );
}
